import React,{ useState, useEffect, useRef, useMemo } from 'react';
import MaterialTable from 'material-table';
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { downloadCsv } from '../common/sharedFunctions';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PersonIcon from '@mui/icons-material/Person';

export default function Users() {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const {
    addUser,
    editUser,
    deleteUser,
    checkUserExists,
    fetchUsers
  } = api;
  const [data, setData] = useState([]);
  const users = useSelector(state => state.usersdata.users);
  const dispatch = useDispatch();
  const loaded = useRef(false);

  useEffect(()=>{
    // Real-time listener - updates instantly when data changes
    dispatch(fetchUsers());
  },[dispatch,fetchUsers]);

  useEffect(()=>{
    if(users){
      setData(users.filter(user => user.usertype ==='admin'));
    }else{
      setData([]);
    }
    loaded.current = true;
  },[users]);

  // Uber-style columns
  const columns = [
    { title: t('admin'), field: 'firstName',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {rowData.profile_image ? (
            <img alt='Profile' src={rowData.profile_image} style={{width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid #F3F4F6'}}/>
          ) : (
            <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PersonIcon sx={{ color: '#9CA3AF', fontSize: 22 }} />
            </Box>
          )}
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
              {rowData.firstName} {rowData.lastName}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
              {settings.AllowCriticalEditsAdmin ? rowData.mobile : "Hidden"}
            </Typography>
          </Box>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('email'), field: 'email', editable:'onAdd',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151' }}>
          {settings.AllowCriticalEditsAdmin ? rowData.email : "Hidden"}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('account_approve'), field: 'approved', type:'boolean', initialEditValue: true,
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FiberManualRecordIcon sx={{ fontSize: 10, color: rowData.approved ? '#10B981' : '#F59E0B' }} />
          <Typography sx={{ fontSize: 14, color: '#374151' }}>
            {rowData.approved ? (t('approved') || 'Approved') : (t('pending') || 'Pending')}
          </Typography>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
  ];

  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filtered data
  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(d =>
      (d.firstName && d.firstName.toLowerCase().includes(query)) ||
      (d.lastName && d.lastName.toLowerCase().includes(query)) ||
      (d.email && d.email.toLowerCase().includes(query))
    );
  }, [data, searchQuery]);

  return (
    !loaded.current? <CircularLoading/>:
    <div>
      {/* Uber-Style Filter Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          p: 2,
          mb: 2,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          alignItems: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <TextField
          size="small"
          placeholder={t('search') || 'Search admins...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            minWidth: 220,
            '& .MuiOutlinedInput-root': {
              borderRadius: 50,
              bgcolor: '#F7F7F7',
              '& fieldset': { border: 'none' },
              '&:hover': { bgcolor: '#EFEFEF' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ width: 1, height: 24, bgcolor: '#E5E7EB', mx: 1 }} />
        <Chip
          label={t('all') || 'All'}
          sx={{
            bgcolor: '#111827',
            color: '#FFFFFF',
            fontWeight: 500,
            fontSize: 13,
            height: 36,
            borderRadius: 50,
            px: 1,
          }}
        />
        <Typography sx={{ ml: 'auto', fontSize: 13, color: '#6B7280' }}>
          {filteredData.length} {t('alladmin') || 'admins'}
        </Typography>
      </Box>

    <MaterialTable
      icons={TableIcons}
      title={null}
      columns={columns}
      style={{
        direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
        borderRadius: 12,
        boxShadow: 'none',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
      }}
      data={filteredData}
      onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
      options={{
        ...tableOptions,
        search: false,
        toolbar: false,
        showTitle: false,
        exportCsv: (columns, data) => {
          let hArray = [];
          const headerRow = columns.map(col => {
            if (typeof col.title === 'object') {
              return col.title.props.text;
            }
            hArray.push(col.field);
            return col.title;
          });
          const dataRows = data.map(({ tableData, ...row }) => {
            row.createdAt = new Date(row.createdAt).toLocaleDateString() + ' '+ new Date(row.createdAt).toLocaleTimeString()
            let dArr = [];
            for(let i=0;i< hArray.length; i++) {
              dArr.push(row[hArray[i]]);
            }
            return Object.values(dArr);
          })
          const { exportDelimiter } = ",";
          const delimiter = exportDelimiter ? exportDelimiter : ",";
          const csvContent = [headerRow, ...dataRows].map(e => e.join(delimiter)).join("\n");
          const csvFileName = 'download.csv';
          downloadCsv(csvContent, csvFileName);
        },
        exportButton: {
          csv: settings.AllowCriticalEditsAdmin,
          pdf: false,
        },
        maxColumnSort: "all_columns",
        rowStyle: rowData => ({
          backgroundColor: (selectedRow === rowData.tableData.id) ? '#F3F4F6' : '#fff'
        }),
      }}
      localization={{body:{
        addTooltip: (t('add')),
        deleteTooltip: (t('delete')),
        editTooltip: (t('edit')),
        emptyDataSourceMessage: (
          (t('blank_message'))
      ),
      editRow: { 
        deleteText: (t('delete_message')),
        cancelTooltip: (t('cancel')),
        saveTooltip: (t('save')) 
          },
        },
        toolbar: {
          searchPlaceholder: (t('search')),
          exportTitle: (t('export')),
        },
        header: {
          actions: (t('actions')) 
      },
      pagination: {
        labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
       // labelRowsSelect: (t('rows')),
        firstTooltip: (t('first_page_tooltip')),
        previousTooltip: (t('previous_page_tooltip')),
        nextTooltip: (t('next_page_tooltip')),
        lastTooltip: (t('last_page_tooltip'))
      },
      }}
      editable={{
        onRowAdd: newData =>
        new Promise((resolve,reject) => {
          setTimeout(() => {
            checkUserExists(newData).then((res) => {
              if (res.users && res.users.length > 0) {
                alert(t('user_exists'));
                reject();
              }
              else if(!(newData && newData.firstName)){
                alert(t('proper_input_name'));
                reject();
              }
              else if(res.error){
                alert(t('email_or_mobile_issue'));
                reject();
              }
              else{
                newData['usertype'] = 'admin';
                newData['createdAt'] = new Date().getTime();
                dispatch(addUser(newData));
                // Real-time listener will auto-update
                resolve();
              }
            });
          }, 600);
        }),
        onRowUpdate: (newData, oldData) =>
          new Promise((resolve,reject)=> {
            setTimeout(() => {
              if(!(newData && newData.firstName)){
                alert(t('proper_input_name'));
                reject();
              }else {
                resolve();
                if(newData !== oldData){
                  delete newData.tableData;
                  dispatch(editUser(oldData.id,newData));
                  // Real-time listener will auto-update
                }
              }
            }, 600);
          }),
        onRowDelete: oldData =>
          settings.AllowCriticalEditsAdmin?
          new Promise(resolve => {
            setTimeout(() => {
              resolve();
              dispatch(deleteUser(oldData.id));
              // Real-time listener will auto-update
            }, 600);
          })
          :
          new Promise(resolve => {
            setTimeout(() => {
              resolve();
              alert(t('demo_mode'));
            }, 600);
          })
      }}
    />
    </div>
  );
}
